import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import {
  FaSearch, FaFilePdf, FaUpload, FaTimes, FaSpinner, FaDownload, FaTrash, FaBookOpen
} from 'react-icons/fa';
import { AuthContext } from '../../components/AuthContext';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0, n = bytes;
  while (n >= 1024 && i < units.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
};

const formatDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const highlightText = (text, query) => {
  if (!query || !text) return text;
  const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'ig'));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i} className="bg-yellow-300 dark:bg-yellow-500 dark:text-slate-900 px-0.5 rounded">{part}</mark>
      : <span key={i}>{part}</span>
  );
};

const PDFCatalogs = () => {
  const { user } = useContext(AuthContext);
  const [catalogs, setCatalogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  const displayed = searchResults !== null ? searchResults : catalogs;
  const isDark = localStorage.getItem('darkMode') === 'true';

  const fetchCatalogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/pdf-catalogs');
      setCatalogs(res.data || []);
    } catch (e) {
      Swal.fire('Error', 'Failed to load PDF catalogs', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCatalogs();
  }, [fetchCatalogs]);

  // Debounced search
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      setSearchLoading(false);
      return undefined;
    }
    const t = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await api.get('/pdf-catalogs/search', { params: { q } });
        setSearchResults(res.data || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const openPdf = (catalog) => {
    const token = localStorage.getItem('token');
    let url = `${API_BASE}/pdf-catalogs/${catalog._id}/view?token=${encodeURIComponent(token || '')}`;
    if (catalog && catalog.firstPage) {
      url += `#page=${catalog.firstPage}`;
    }
    window.open(url, '_blank', 'noopener');
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      Swal.fire('Invalid File', 'Please select a PDF file', 'warning');
      return;
    }
    if (file.size > 1024 * 1024 * 1024) {
      Swal.fire('File Too Large', 'Maximum PDF size is 1 GB', 'warning');
      return;
    }
    setSelectedFile(file);
    if (!uploadTitle) setUploadTitle(file.name.replace(/\.pdf$/i, ''));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files && e.dataTransfer.files[0]);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      Swal.fire('No File', 'Please select a PDF file first', 'warning');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('pdf', selectedFile);
      formData.append('title', uploadTitle.trim());
      await api.post('/pdf-catalogs/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
        onUploadProgress: (e) => {
          if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        },
      });
      Swal.fire('Uploaded!', 'PDF catalog uploaded successfully', 'success');
      setShowUpload(false);
      setUploadTitle('');
      setSelectedFile(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchCatalogs();
    } catch (e) {
      Swal.fire('Upload Failed', e.response?.data?.msg || 'Something went wrong while uploading', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (catalog) => {
    const result = await Swal.fire({
      title: 'Delete PDF Catalog?',
      text: `"${catalog.title || catalog.fileName}" will be permanently removed`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/pdf-catalogs/${catalog._id}`);
      Swal.fire('Deleted', 'PDF catalog removed', 'success');
      fetchCatalogs();
      if (searchResults !== null) setSearchResults(searchResults.filter((c) => c._id !== catalog._id));
    } catch (e) {
      Swal.fire('Failed', e.response?.data?.msg || 'Could not delete catalog', 'error');
    }
  };

  return (
    <div className={`p-4 md:p-6 ${isDark ? 'text-slate-100' : 'text-slate-800'}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            PDF Catalogs
          </h1>
          <p className="text-sm opacity-70 mt-0.5">Upload and search inside the text of your product catalogs</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg shadow-lg shadow-blue-600/30 transition-all hover:shadow-blue-600/50 hover:scale-[1.02]"
        >
          <FaUpload /> Upload PDF
        </button>
      </div>

      {/* Search bar */}
      <div className="mb-5">
        <div className={`relative flex items-center rounded-xl shadow-md overflow-hidden ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-200'}`}>
          <FaSearch className="absolute left-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search text inside all PDF catalogs... (e.g. product name, dimensions, HSN)"
            className="w-full pl-11 pr-10 py-3 outline-none bg-transparent text-sm"
          />
          {searchLoading && <FaSpinner className="absolute right-4 animate-spin text-blue-500" />}
          {!searchLoading && searchQuery && (
            <button onClick={() => setSearchQuery('')} className="absolute right-4 text-gray-400 hover:text-gray-600">
              <FaTimes />
            </button>
          )}
        </div>
        {searchResults !== null && (
          <p className="text-xs mt-2 opacity-70">
            {searchResults.length} result{searchResults.length === 1 ? '' : 's'} for "<span className="font-semibold">{searchQuery}</span>"
            {' '}
            <button onClick={() => setSearchQuery('')} className="text-blue-600 underline">Clear search</button>
          </p>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex justify-center items-center py-20 gap-2 text-gray-500">
          <FaSpinner className="animate-spin" /> Loading catalogs...
        </div>
      ) : !displayed.length ? (
        <div className={`rounded-xl border border-dashed py-20 text-center ${isDark ? 'border-slate-700 bg-slate-800/40' : 'border-gray-300 bg-white'}`}>
          <FaFilePdf className="mx-auto text-5xl text-gray-300 mb-4" />
          <p className="text-lg font-semibold">
            {searchResults !== null ? 'No matching documents' : 'No PDF catalogs yet'}
          </p>
          <p className="text-sm opacity-70 mt-1">
            {searchResults !== null
              ? `Nothing found containing "${searchQuery}". Try a different keyword.`
              : 'Upload your first PDF catalog to start searching its content.'}
          </p>
          {searchResults === null && (
            <button
              onClick={() => setShowUpload(true)}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <FaUpload /> Upload first catalog
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayed.map((c) => (
            <div
              key={c._id}
              className={`group flex flex-col rounded-xl shadow-md overflow-hidden transition-all hover:shadow-xl hover:-translate-y-1 cursor-pointer ${
                isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white border border-gray-100'
              }`}
              onClick={() => openPdf(c)}
            >
              <div className="flex items-center gap-3 p-4 bg-gradient-to-br from-red-50 to-white dark:from-red-950/40 dark:to-slate-800 border-b border-gray-100 dark:border-slate-700">
                <div className="p-3 bg-red-100 dark:bg-red-900/50 rounded-lg shrink-0">
                  <FaFilePdf className="text-red-500 text-2xl" />
                </div>
                <div className="min-w-0">
                  <h3 className="font-semibold truncate">{c.title || c.fileName}</h3>
                  <p className="text-xs opacity-60 truncate">{c.fileName}</p>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                {c.snippet ? (
                  <p className="text-xs leading-relaxed opacity-80 bg-yellow-50 dark:bg-yellow-900/20 rounded p-2 line-clamp-3">
                    {highlightText(c.snippet, searchQuery)}
                  </p>
                ) : (
                  <div className="flex items-center gap-3 text-xs opacity-70">
                    <span>{c.pageCount || '—'} pages</span>
                    <span>{formatBytes(c.fileSize)}</span>
                  </div>
                )}
                <div className="mt-auto flex items-center justify-between text-xs opacity-60 pt-2">
                  <span>{formatDate(c.createdAt)}</span>
                  <span>by {c.uploadedBy?.name || (user?.user?._id === c.uploadedBy ? 'You' : '—')}</span>
                </div>
              </div>
              <div className="flex border-t border-gray-100 dark:border-slate-700">
                <button
                  onClick={(e) => { e.stopPropagation(); openPdf(c); }}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950/40 font-medium transition-colors"
                >
                  <FaBookOpen /> Open PDF
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(c); }}
                  className="px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
                  title="Delete"
                >
                  <FaTrash />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => !uploading && setShowUpload(false)}>
          <div
            className={`w-full max-w-lg rounded-2xl shadow-2xl overflow-hidden ${isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-700">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <FaFilePdf className="text-red-500" /> Upload PDF Catalog
              </h2>
              <button onClick={() => setShowUpload(false)} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500">
                <FaTimes />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
                  selectedFile
                    ? 'border-green-400 bg-green-50 dark:bg-green-950/20'
                    : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50 dark:border-slate-600 dark:hover:bg-slate-700/50'
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  className="hidden"
                  onChange={(e) => handleFileSelect(e.target.files && e.target.files[0])}
                />
                {selectedFile ? (
                  <div className="flex items-center justify-center gap-3">
                    <div className="text-left min-w-0">
                      <p className="font-semibold truncate">{selectedFile.name}</p>
                      <p className="text-xs opacity-70">
                        {formatBytes(selectedFile.size)} — click to change
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    <FaUpload className="mx-auto text-3xl text-gray-400 mb-3" />
                    <p className="font-medium">Drop your PDF here or <span className="text-blue-600 underline">browse</span></p>
                    <p className="text-xs opacity-60 mt-1">Max size 1 GB</p>
                  </>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide mb-1 opacity-70">Title (optional)</label>
                <input
                  type="text"
                  value={uploadTitle}
                  onChange={(e) => setUploadTitle(e.target.value)}
                  placeholder="e.g. 2026 Ceramic & Vitrified Tiles Catalog"
                  className={`w-full px-3 py-2.5 rounded-lg border outline-none text-sm focus:ring-2 focus:ring-blue-500 ${
                    isDark ? 'bg-slate-700 border-slate-600' : 'bg-gray-50 border-gray-300'
                  }`}
                />
              </div>

              {uploading && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span>Uploading & extracting text...</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className="h-2 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-blue-600 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                </div>
              )}

              <button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold transition-all ${
                  uploading || !selectedFile
                    ? 'bg-gray-300 dark:bg-slate-700 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-600/30'
                }`}
              >
                {uploading ? (
                  <><FaSpinner className="animate-spin" /> Processing...</>
                ) : (
                  <><FaUpload /> Upload PDF</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PDFCatalogs;